import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { claudeTokens } from '@/styles/claudeTokens';
import { StickyBottomBar } from '@/components/common/StickyBottomBar';
import { useListHeaderHeight } from '@/components/common/organism/ListPageLayout';

/**
 * 긴 입력 폼용 공용 내비게이션 — 상단 섹션 탭(FormSectionTabs)과 하단 고정 저장 바(FormSaveBar).
 *
 * 긴 폼은 "지금 어디를 쓰고 있는지"와 "저장 버튼이 어디 있는지"를 놓치기 쉽다.
 * 탭은 현재 섹션을 알려 주고 원하는 곳으로 바로 데려가며, 저장 바는 끝까지 내리지 않아도 항상 손에 닿게 한다.
 */

export interface FormSection {
    /** 섹션 제목 요소의 id — 이 id 로 스크롤 위치를 잡는다 */
    id: string;
    /** 탭에 보일 짧은 이름 */
    label: string;
}

/** 섹션 제목이 탭 줄에 딱 붙지 않도록 주는 여유 */
const SECTION_SCROLL_GAP_PX: number = 12;
/** 탭을 눌러 부드럽게 이동하는 동안 scrollspy 를 쉬게 하는 시간 — 지나치는 섹션으로 활성 탭이 깜빡이지 않게 */
const TAP_SCROLL_LOCK_MS: number = 700;
/** 칸 포커스로 이동할 때 scrollspy 를 쉬는 시간 — 얼럿을 닫은 뒤 부드러운 스크롤이 길게 이어져도 탭이 되돌아가지 않게 */
const FOCUS_SCROLL_LOCK_MS: number = 1500;
/** 탭 이동 시 남겨 둔 12px 여백까지 포함해야 이동 완료 뒤 이전 탭으로 되돌아가지 않는다. */
const ACTIVE_LINE_SLACK_PX: number = SECTION_SCROLL_GAP_PX + 1;

const TabBar = styled.div<{ topOffset: number }>`
    position: sticky;
    top: ${({ topOffset }) => topOffset}px;
    z-index: 20;
    display: flex;
    gap: 4px;
    overflow-x: auto;
    background: ${claudeTokens.color.surface};
    border-bottom: 1px solid ${claudeTokens.color.border};

    /* 가로로 넘겨 볼 수 있지만 스크롤바는 보이지 않게 */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: 768px) {
        /* 본문 좌우 여백(#app-content 의 0.5rem)을 상쇄해 화면 폭을 꽉 채운다 */
        margin-left: -0.5rem;
        margin-right: -0.5rem;
        padding: 0 0.5rem;
    }
`;

const TabButton = styled.button<{ active: boolean }>`
    flex: 0 0 auto;
    padding: 12px 14px;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 14px;
    font-weight: ${({ active }) => (active ? 600 : 500)};
    color: ${({ active }) => (active ? claudeTokens.color.accent : claudeTokens.color.text2)};
    white-space: nowrap;
    cursor: pointer;
    /* 밑줄은 항상 자리를 차지하게 두어 활성 전환 때 높이가 흔들리지 않게 한다 */
    border-bottom: 2px solid ${({ active }) => (active ? claudeTokens.color.accent : 'transparent')};
    margin-bottom: -1px;
`;

/**
 * 긴 폼의 섹션 탭 — 앱 헤더 바로 아래에 붙어 현재 섹션을 알려 주고, 누르면 그 섹션으로 이동한다.
 *
 * @param sections 섹션 제목 요소의 id 와 탭 이름. id 는 폼의 섹션 제목에 직접 달아 둔다.
 * @param className 화면이 좌우 여백을 따로 맞출 때 `styled(FormSectionTabs)` 로 덧붙이는 스타일
 *
 * @example
 * <FormSectionTabs sections={[{ id: 'resume-basic', label: '기본 정보' }]} />
 * ...
 * <SectionTitle id="resume-basic">기본 정보</SectionTitle>
 */
export const FormSectionTabs: React.FC<{ sections: FormSection[]; className?: string }> = ({ sections, className }) => {
    const barRef = useRef<HTMLDivElement | null>(null);
    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    /** 탭을 눌러 이동하는 동안에는 scrollspy 를 무시할 시각 */
    const tapLockUntilRef = useRef<number>(0);
    const headerHeight: number = useListHeaderHeight(true);
    const [barHeight, setBarHeight] = useState<number>(0);
    const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');

    // 탭 높이도 관찰한다. 화면 폭 변경 뒤 React가 헤더를 다시 배치하는 시점은 공용 훅이 따라간다.
    useEffect(() => {
        const bar = barRef.current;
        if (!bar) return;
        const measure = (): void => {
            setBarHeight(bar.getBoundingClientRect().height);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(bar);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, []);

    const stickyOffset: number = headerHeight + barHeight;

    // 탭을 눌러 이동했을 때 섹션 제목이 헤더·탭 뒤로 숨지 않게 한다
    useEffect(() => {
        sections.forEach((section: FormSection) => {
            const element: HTMLElement | null = document.getElementById(section.id);
            if (element) element.style.scrollMarginTop = `${stickyOffset + SECTION_SCROLL_GAP_PX}px`;
        });
    }, [sections, stickyOffset]);

    /** 고정 영역 바로 아래 선을 이미 지나간 마지막 섹션이 "지금 보고 있는 섹션" */
    const resolveActive = useCallback((): void => {
        if (Date.now() < tapLockUntilRef.current) return;
        const line: number = stickyOffset + ACTIVE_LINE_SLACK_PX;
        let current: string = sections[0]?.id ?? '';
        sections.forEach((section: FormSection) => {
            const element: HTMLElement | null = document.getElementById(section.id);
            if (element && element.getBoundingClientRect().top <= line) current = section.id;
        });
        setActiveId(current);
    }, [sections, stickyOffset]);

    // 스크롤 이벤트로 판정한다(프레임당 1회로 묶음). IntersectionObserver 는 쓰지 않는다 —
    // 섹션 앵커 일부는 제목이 아니라 화면보다 큰 묶음 요소라, 그 위쪽 끝이 기준선을 넘는 순간에는 교차 비율 임계(0·1)를
    // 지나지 않아 콜백이 오지 않고 활성 탭이 한 박자 늦게 바뀐다. 섹션 몇 개의 위치만 재므로 비용은 무시할 만하다.
    useEffect(() => {
        resolveActive();
        let frame: number = 0;
        const onScroll = (): void => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                resolveActive();
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [resolveActive]);

    // 입력 칸(data-field)에 포커스가 들어오면 그 칸이 속한 섹션 탭을 바로 켠다.
    // 검증 실패로 맨 아래 칸(참가비 등)에 포커스할 때 페이지 끝이라 scrollspy 기준선을 못 넘어 위 섹션에 머무는 문제를 막는다.
    // 칸이 속한 섹션 = 문서 순서상 그 칸보다 앞에 있는 마지막 섹션 앵커. 스크롤이 끝날 때까지 scrollspy 는 쉰다.
    useEffect(() => {
        const onFocusIn = (event: FocusEvent): void => {
            const target: HTMLElement | null = event.target instanceof HTMLElement ? event.target : null;
            const field: HTMLElement | null = target?.closest<HTMLElement>('[data-field]') ?? null;
            if (!field) return;
            let owner: string | null = null;
            sections.forEach((section: FormSection) => {
                const anchor: HTMLElement | null = document.getElementById(section.id);
                if (anchor && (anchor === field || anchor.contains(field)
                    || (anchor.compareDocumentPosition(field) & Node.DOCUMENT_POSITION_FOLLOWING))) {
                    owner = section.id;
                }
            });
            if (!owner) return;
            setActiveId(owner);
            tapLockUntilRef.current = Date.now() + FOCUS_SCROLL_LOCK_MS;
        };
        document.addEventListener('focusin', onFocusIn);
        return () => document.removeEventListener('focusin', onFocusIn);
    }, [sections]);

    // 활성 탭이 탭 줄 밖으로 나가 있으면 줄 안에서 가로로만 옮겨 준다 (scrollIntoView 는 페이지 전체를 움직여 쓰지 않는다)
    useEffect(() => {
        const bar: HTMLDivElement | null = barRef.current;
        const tab: HTMLButtonElement | undefined = tabRefs.current.get(activeId);
        if (!bar || !tab) return;
        const left: number = tab.offsetLeft - (bar.clientWidth - tab.clientWidth) / 2;
        bar.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }, [activeId]);

    const handleTabClick = (id: string): void => {
        const element: HTMLElement | null = document.getElementById(id);
        if (!element) return;
        setActiveId(id);
        tapLockUntilRef.current = Date.now() + TAP_SCROLL_LOCK_MS;
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <TabBar ref={barRef} className={className} topOffset={headerHeight} role="navigation" aria-label="입력 항목">
            {sections.map((section: FormSection) => (
                <TabButton
                    key={section.id}
                    ref={(node: HTMLButtonElement | null) => {
                        if (node) tabRefs.current.set(section.id, node);
                        else tabRefs.current.delete(section.id);
                    }}
                    type="button"
                    // 탭 패널을 바꾸는 탭이 아니라 같은 화면 안의 위치로 가는 링크라 tab 역할 대신 aria-current 로 현재 위치만 알린다
                    aria-current={activeId === section.id ? 'true' : undefined}
                    active={activeId === section.id}
                    onClick={() => handleTabClick(section.id)}
                >
                    {section.label}
                </TabButton>
            ))}
        </TabBar>
    );
};

const SaveBar = styled(StickyBottomBar)`
    /* 데스크톱은 버튼을 원래 크기로 오른쪽에 모은다 (폼 버튼들이 width:100%·flex:1 을 갖고 있어 눌러 준다) */
    justify-content: flex-end;

    > * {
        flex: 0 0 auto;
        width: auto;
        margin: 0;
    }

    @media (max-width: 768px) {
        /* 모바일에서는 버튼을 가로로 꽉 채워 엄지로 누르기 쉽게 */
        > * {
            flex: 1 1 0;
            min-width: 0;
        }

        /* 글 입력 중(화면 키보드)에는 숨긴다 — 키보드 바로 위로 올라와 입력칸을 가리거나(안드로이드), 하단 탭이 숨은 자리에
           떠 아래 본문이 비쳤다(iOS 하드웨어 키보드). 신호는 Header 의 body[data-input-focused](하단 탭과 같다 — 포커스 + 키보드가 실제로 떠 있을 때, common/inputKeyboard.ts).
           자리는 두고 안 보이게만 — 빼면 문서가 줄 높이만큼 짧아져 페이지 끝 입력칸이 아래로 튄다(2026-10-06 측정 97px) */
        body[data-input-focused='true'] & {
            visibility: hidden;
            pointer-events: none;
        }
    }
`;

/**
 * 긴 폼의 하단 저장 바 — 화면 아래(모바일은 하단 탭바 위)에 붙어 저장 버튼이 항상 손에 닿게 한다.
 *
 * 취소 버튼은 두지 않는다 — 공용 헤더의 뒤로가기와 스와이프 뒤로가기가 이미 그 역할을 한다.
 *
 * @param children 저장/등록 등 폼이 원래 가지고 있던 주 동작 버튼(들)
 * @param className 화면이 좌우 여백을 따로 맞출 때 `styled(FormSaveBar)` 로 덧붙이는 스타일
 *
 * @example
 * <FormSaveBar>
 *     <SaveButton onClick={handleSave}>저장</SaveButton>
 * </FormSaveBar>
 */
export const FormSaveBar: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <SaveBar className={className}>{children}</SaveBar>
);
